/**
 * **対戦のパネル**（Issue #552。攻撃宣言 `effects/attack.js` の大きいパネルと、盤に残る小さいパネルの印
 * `drawObjects.js::battlePanel` が共有する）。
 *
 * 帯の中の大きいパネルは縮みながら小さいパネルの場所へ移り、そこで**盤に置いた印に引き継ぐ**。継ぎ目を見せないため、
 * **絵は 2D canvas の 1 つの描き方（ここ）で描き**、three はそれをテクスチャにして貼るだけ、印は同じ描き方を縮めて描く。
 *
 * 絵は 2 枚の層に分けてある:
 * - `base`  … 枠・窓の地・カードのイラスト（裏側はスリーブと ?）・その人の色の線・**比べる数字**
 * - `small` … 名前・★・ATK / DEF の見出し・表示形式（ATTACK / DEFENSE / SET）
 * 小さいパネルにも両方を描く。**小さいパネルの文字は 46px の下限を外してある**（読めなくてもよい、文字がなくなると寂しい。ユーザー 2026-09-26）
 *
 * 座標は canvas の左上が原点（y 下向き）。canvas は幅 `PANEL.w`・高さ `PANEL.h`（上の `PANEL.nameH` が名前の段、その下が枠）。
 * `spec.side === 1`（右側のパネル）は枠を左右反転し（イラストが外側）、文字は読む順のままイラストの側に寄せる。
 *
 * three を import しない（純関数 ＋ 2D の context）。
 */
import attackPanel from './attackPanel.png';
import { FONT, statBase, statColor } from './common';
import { imageReady, themeImage } from './images';

/**
 * 枠の窓と段（`attackPanel.png` 1320×679 を `scripts/hud-ornament-key.py` で抜いて測った値。幅・高さに対する比）。
 * 右の窓の上下は枠の暗い板（不透明）で、そこにも文字を置く。
 */
const WIN = {
  art: { l: 82 / 1320, r: 506 / 1320, t: 82 / 679, b: 596 / 679 },
  text: { l: 606 / 1320, r: 1238 / 1320, t: 153 / 679, b: 491 / 679 },
  rows: { top: 97 / 679, big: 322 / 679, bottom: 561 / 679 },
};

const FW = 880;                    // 枠の幅
const FH = FW / (1320 / 679);      // 枠の高さ
const NAME_H = 100;                // 上の名前の段

/** パネルの canvas の大きさ（大きいとき）と、小さいパネルの倍率の上限（魔法・罠ゾーンに掛からない範囲で置ける最大にする） */
export const PANEL = Object.freeze({ w: FW, h: FH + NAME_H, frameH: FH, nameH: NAME_H, mini: 0.65 });

/** カードの画像のうちイラストの部分（`kit.CARD_ART` と同じ値。three を import しないので写してある） */
const CARD_ART = { l: 125 / 743, r: 1 - 134 / 743, t: 268 / 1080, b: 1 - 331 / 1080 };

/** 小さいパネルの上端（HUD の手札の段より下） */
const MINI_TOP = 300;
const MINI_MARGIN = 10;

/** 枠の画像（three は URL を `kit.urlTexture` で、canvas は `frameImage()` で） */
export const FRAME_URL = attackPanel;
export const frameImage = () => themeImage(attackPanel);

/** 数字の今の色（大きい数字は金・下の段は白） */
const NUMBER_COLOR = '#ffd21a';
const SUB_COLOR = '#f2f4f8';

/**
 * **パネルに出すもの**（描くのに要る値だけ。印の `data` にもこのまま入る）。
 *
 * @param who 盤面から引いたカード `{ code, faceUp, defense, atk, def, level, baseAtk, baseDef }`
 *   （`baseAtk` / `baseDef` は元々の値。数字の色 = 上がっていれば赤・下がっていれば青・同じか分からなければ今の色。Issue #584）
 * @param opts `{ attacker, player, side, color, name, mode }`。`mode: 'summon'`（召喚成功。Issue #578）は比べる相手が
 *   居ないので **大きい数字 = ATK、その下の段 = DEF**（`sub`）。既定の `'battle'`（攻撃宣言）は比べる数字 1 つ（`sub` は null）
 */
export function panelSpec(who, { attacker, player, side, color, name = '', mode = 'battle' }) {
  const faceDown = who?.faceUp === false;
  const summon = mode === 'summon';
  // 比べる数字: 攻撃側は ATK、対象は攻撃表示なら ATK・守備表示（裏側も）なら DEF。召喚は常に ATK（DEF は下の段）
  const cmpDef = !summon && !attacker && (Boolean(who?.defense) || faceDown);
  const v = cmpDef ? who?.def : who?.atk;
  const lv = faceDown ? null : who?.level;
  const num = (x) => (faceDown || !Number.isFinite(x) ? '?' : String(x));
  const tone = (x, base, fallback) => (faceDown ? fallback : statColor(x, base, fallback));
  return {
    code: faceDown ? null : (who?.code ?? null),
    player: player === 1 ? 1 : 0,
    side: side === 1 ? 1 : -1,
    color,
    faceDown,
    name: faceDown ? '？？？' : (name || '？？？'),
    stars: Number.isFinite(lv) && lv > 0 ? (lv <= 12 ? '★'.repeat(lv) : `★×${lv}`) : '★ ?',
    label: cmpDef ? 'DEF' : 'ATK',
    number: num(v),
    /** 大きい数字の色（今の色は金。元々の値より上なら赤・下なら青） */
    numberColor: tone(v, cmpDef ? who?.baseDef : who?.baseAtk, NUMBER_COLOR),
    /** 大きい数字の下の段（召喚のパネルだけ。`{ label: 'DEF', number, color }`。攻撃宣言は null） */
    sub: summon ? { label: 'DEF', number: num(who?.def), color: tone(who?.def, who?.baseDef, SUB_COLOR) } : null,
    position: faceDown ? 'SET' : (who?.defense ? 'DEFENSE' : 'ATTACK'),
    positionColor: faceDown ? '#c27cff' : (who?.defense ? '#5aa8ff' : '#ff4a3a'),
  };
}

/**
 * **盤面からパネルに出す値を引く**（`who` = payload の `attacker` / `target`。`uid` で盤のカードを引き、
 * code・表裏・表示形式・ATK / DEF・レベルを足す。盤に無ければ payload の値のまま）。
 * 攻撃宣言の帯と、盤に残る小さいパネル（描くたびに今の盤面で引き直す）が使う。
 */
export function whoFromBoard(who, board) {
  if (!who) return null;
  const card = who.uid ? board?.byUid?.(who.uid)?.card ?? null : null;
  const st = card?.stats ?? null;
  const num = (v) => (Number.isFinite(v) ? v : null);
  const base = statBase(st);
  return {
    ...who,
    code: card?.code ?? who.code ?? null,
    faceUp: card ? Boolean(card.faceUp) : (who.faceUp ?? true),
    defense: card ? Boolean(card.defense) : Boolean(who.defense),
    atk: num(st?.atk) ?? who.atk ?? null,
    def: num(st?.def) ?? who.def ?? null,
    level: num(st?.level) ?? who.level ?? null,
    // 元々の値（Issue #584。数字の色に使う。盤に無ければ payload の値）
    baseAtk: base.atk ?? who.baseAtk ?? null,
    baseDef: base.def ?? who.baseDef ?? null,
  };
}

/** 文字の設定（`kit.textTexture` と同じ: 太さ 900・黒の縁 size × 0.14） */
const font = (size) => `900 ${size}px ${FONT}`;

/** 幅を測る（入らなければ横に詰めた幅） */
function measure(ctx, str, size, maxW) {
  ctx.font = font(size);
  const w = ctx.measureText?.(str)?.width ?? str.length * size * 0.6;
  const sx = Math.min(1, maxW / Math.max(1, w));
  return { str, size, w: w * sx, sx };
}

/** 左端を x に揃えて描く */
function draw(ctx, m, x, y, color) {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(m.sx, 1);
  ctx.font = font(m.size);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  ctx.lineWidth = m.size * 0.14 * 2;
  ctx.strokeStyle = '#050506';
  ctx.strokeText?.(m.str, 0, 0);
  ctx.fillStyle = color;
  ctx.fillText(m.str, 0, 0);
  ctx.restore();
}

/**
 * **形だけの並べ方**（文字の幅を測らない部分。`layout` と `panelAnchors` が共有する）。
 *
 * 召喚のパネル（`spec.sub` あり）は、文字の窓の中を**上に大きい ATK・下に DEF の段**の 2 段にし、その人の色の線は窓の下端に寄せる。
 * 攻撃宣言（`sub` 無し）の値は今までどおり（絵が変わらない）。
 */
function geometry(spec) {
  const mir = spec.side === 1;
  const X = (fx) => (mir ? FW * (1 - fx) : FW * fx);
  const Y = (fy) => NAME_H + fy * FH;
  const dir = mir ? -1 : 1;
  const xs = (a, b) => [Math.min(a, b), Math.max(a, b)];
  const [ax0, ax1] = xs(X(WIN.art.l), X(WIN.art.r));
  const [tx0, tx1] = xs(X(WIN.text.l), X(WIN.text.r));
  const art = { x: ax0, y: Y(WIN.art.t), w: ax1 - ax0, h: Y(WIN.art.b) - Y(WIN.art.t) };
  const txt = { x: tx0, y: Y(WIN.text.t), w: tx1 - tx0, h: Y(WIN.text.b) - Y(WIN.text.t) };
  const twoRows = Boolean(spec.sub);
  return {
    mir, X, Y, dir, art, txt,
    inner: txt.w - 56,
    x0: X(WIN.text.l) + dir * 20,   // イラストの側の端
    yTop: Y(WIN.rows.top),
    yBig: twoRows ? txt.y + txt.h * 0.34 : Y(WIN.rows.big),
    ySub: twoRows ? txt.y + txt.h * 0.77 : null,
    yBot: Y(WIN.rows.bottom),
    underY: txt.y + txt.h * (twoRows ? 0.93 : 0.86) - 3,
  };
}

/** 並べ方（どちらの層も同じ値を使う） */
function layout(ctx, spec) {
  const G = geometry(spec);
  const { mir, X, dir, inner, x0 } = G;
  /** 読む順に並べてイラストの側へ寄せたときの各項目の左端 */
  const row = (items) => {
    const total = items.reduce((a, [m, gap]) => a + m.w + (gap ?? 0), 0);
    let x = dir === 1 ? x0 : x0 - total;
    return items.map(([m, gap]) => { const left = x; x += m.w + (gap ?? 0); return left; });
  };
  const label = measure(ctx, spec.label, 60, 140);
  const number = measure(ctx, spec.number, 132, inner - label.w - 18);
  const [labelX, numberX] = row([[label, 18], [number]]);
  const stars = measure(ctx, spec.stars, 46, inner);
  const [starsX] = row([[stars]]);
  const position = measure(ctx, spec.position, 46, inner);
  const [positionX] = row([[position]]);
  const name = measure(ctx, spec.name, 60, FW * 0.9);
  const nameX = mir ? X(0.03) - name.w : X(0.03);
  const uw = Math.min(FW * 0.9, name.w + 40);
  // 召喚のパネルの下の段（DEF の見出し 46px ＋ 数字 60px）
  let sub = null;
  if (spec.sub) {
    const subLabel = measure(ctx, spec.sub.label, 46, 120);
    const subNumber = measure(ctx, spec.sub.number, 60, inner - subLabel.w - 14);
    const [subLabelX, subNumberX] = row([[subLabel, 14], [subNumber]]);
    sub = { label: subLabel, labelX: subLabelX, number: subNumber, numberX: subNumberX, y: G.ySub };
  }
  return {
    ...G,
    label, labelX, number, numberX, stars, starsX, position, positionX, sub,
    name, nameX, under: { x: mir ? X(0.03) - uw : X(0.03), w: uw },
  };
}

/** 部品の名前（`paintPanel` の `parts` で選ぶ）。`base` の層と `small` の層に分かれている */
export const PANEL_PARTS = Object.freeze({
  base: Object.freeze(['ground', 'art', 'underline', 'frame', 'number']),
  small: Object.freeze(['name', 'stars', 'label', 'sub', 'position']),
});

/**
 * **部品の位置**（canvas の座標。文字の幅は測らない＝ctx 不要）。部品を別々に叩き込む演出（召喚成功）が
 * 拡大の中心にする。`name` は名前の段の内側の端、ほかは文字の窓の横の中央とその段の高さ。
 */
export function panelAnchors(spec) {
  const G = geometry(spec);
  const cx = G.txt.x + G.txt.w / 2;
  return {
    name: [G.mir ? FW * 0.97 : FW * 0.03, NAME_H - 46],
    stars: [cx, G.yTop],
    number: [cx, G.yBig],
    sub: [cx, G.ySub ?? G.yBig],
    position: [cx, G.yBot],
  };
}

/** 画像の一部（イラストの切り出し）を箱に cover で描く */
function drawCover(ctx, img, crop, box) {
  const iw = img.naturalWidth || img.videoWidth || img.width || 0;
  const ih = img.naturalHeight || img.videoHeight || img.height || 0;
  if (!(iw > 0 && ih > 0)) return;
  const sx = iw * crop.l; const sy = ih * crop.t;
  const sw = iw * (crop.r - crop.l); const sh = ih * (crop.b - crop.t);
  const k = Math.max(box.w / sw, box.h / sh);
  const dw = sw * k; const dh = sh * k;
  ctx.save();
  ctx.beginPath();
  ctx.rect(box.x, box.y, box.w, box.h);
  ctx.clip();
  ctx.drawImage(img, sx, sy, sw, sh, box.x + (box.w - dw) / 2, box.y + (box.h - dh) / 2, dw, dh);
  ctx.restore();
}

/**
 * **パネルを描く**（canvas の左上が原点。倍率は呼ぶ側が `ctx.scale` で掛ける）。
 *
 * @param layer `'base'` か `'small'`
 * @param imgs `{ art }`（カードの画像か、裏側ならスリーブ。無ければ地の色）
 * @param parts 描く部品（`PANEL_PARTS` の名前の配列）。省略で全部。部品を別々の板にして順に叩き込む演出（召喚成功）が使う
 */
export function paintPanel(ctx, spec, layer, imgs = {}, parts = null) {
  const L = layout(ctx, spec);
  const on = (k) => !parts || parts.includes(k);
  if (layer === 'small') {
    if (on('name')) {
      draw(ctx, L.name, L.nameX, NAME_H - 46, '#ffffff');
      ctx.fillStyle = spec.color;
      ctx.fillRect(L.under.x, NAME_H - 9, L.under.w, 7);
    }
    if (on('stars')) draw(ctx, L.stars, L.starsX, L.yTop, '#ffc21a');
    if (on('label')) draw(ctx, L.label, L.labelX, L.yBig, '#dfe3ea');
    if (on('sub') && L.sub) {
      draw(ctx, L.sub.label, L.sub.labelX, L.sub.y, '#dfe3ea');
      draw(ctx, L.sub.number, L.sub.numberX, L.sub.y, spec.sub.color ?? SUB_COLOR);
    }
    if (on('position')) draw(ctx, L.position, L.positionX, L.yBot, spec.positionColor);
    return;
  }
  if (on('ground')) {
    // 窓の地（絵の窓は暗く、文字の窓は 2 段の硬い影）
    ctx.fillStyle = '#0b0d12';
    ctx.fillRect(L.art.x - 5, L.art.y - 5, L.art.w + 10, L.art.h + 10);
    ctx.fillStyle = '#1a1e28';
    ctx.fillRect(L.txt.x - 5, L.txt.y - 3, L.txt.w + 10, L.txt.h / 2 + 3);
    ctx.fillStyle = '#0c0e14';
    ctx.fillRect(L.txt.x - 5, L.txt.y + L.txt.h / 2, L.txt.w + 10, L.txt.h / 2 + 3);
  }
  // イラスト（裏側はスリーブ ＋ ?）
  const art = imgs.art;
  if (on('art')) {
    if (imageReady(art)) drawCover(ctx, art, CARD_ART, { x: L.art.x - 4, y: L.art.y - 4, w: L.art.w + 8, h: L.art.h + 8 });
    if (spec.faceDown) {
      const q = measure(ctx, '?', 220, L.art.w);
      ctx.save();
      if (imageReady(art)) { ctx.fillStyle = 'rgba(0,0,0,0.35)'; ctx.fillRect(L.art.x, L.art.y, L.art.w, L.art.h); }
      ctx.restore();
      draw(ctx, q, L.art.x + (L.art.w - q.w) / 2, L.art.y + L.art.h / 2, '#e8ebf0');
    }
  }
  if (on('underline')) {
    // 比べる数字の下に、その人の色の硬い線
    ctx.fillStyle = spec.color;
    ctx.fillRect(L.txt.x + L.txt.w * 0.07, L.underY, L.txt.w * 0.86, 6);
  }
  // 枠（右側は左右反転）
  const frame = frameImage();
  if (on('frame') && imageReady(frame)) {
    ctx.save();
    if (L.mir) { ctx.translate(FW, 0); ctx.scale(-1, 1); }
    ctx.drawImage(frame, 0, NAME_H, FW, FH);
    ctx.restore();
  }
  // 比べる数字（大きく金。元々の値より上なら赤・下なら青。Issue #584）
  if (on('number')) draw(ctx, L.number, L.numberX, L.yBig, spec.numberColor ?? NUMBER_COLOR);
}

/** 描き直しの鍵（画像が読めたら描き直す） */
export const panelReady = (imgs = {}) => [imageReady(frameImage()), imageReady(imgs.art)];

/** 魔法・罠ゾーンの外接する箱（写らなければ null） */
function magicTrapBox(zoneQuad, player) {
  const qs = [0, 1, 2, 3, 4].map((i) => zoneQuad?.(`magic_trap:${player}:${i}`)).filter((q) => Array.isArray(q) && q.length === 4);
  if (!qs.length) return null;
  const pts = qs.flat();
  const xs = pts.map((p) => p[0]); const ys = pts.map((p) => p[1]);
  if (![...xs, ...ys].every(Number.isFinite)) return null;
  const x = Math.min(...xs); const y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}

/**
 * **小さいパネルの場所**（舞台のピクセル）。そのプレイヤーの**魔法・罠ゾーンの外側**（盤の外の余白。HUD の手札の段より下）。
 * 魔法・罠の列が画面の左にあれば左の端、右にあれば右の端に寄せ、**そこに入る最大の大きさ**（上限 `PANEL.mini`）にする。
 *
 * @returns `{ x, y, w, h, scale }`（`w` / `h` は canvas 全体を `scale` 倍した大きさ）
 */
export function miniBox(zoneQuad, player, canvas = { w: 1920, h: 1080 }) {
  const mt = magicTrapBox(zoneQuad, player);
  const left = mt ? mt.x + mt.w / 2 < canvas.w / 2 : player !== 1;
  const room = mt ? (left ? mt.x : canvas.w - (mt.x + mt.w)) - MINI_MARGIN * 2 - 4 : PANEL.w * PANEL.mini;
  const scale = Math.max(0.2, Math.min(PANEL.mini, room / PANEL.w));
  const w = PANEL.w * scale; const h = PANEL.h * scale;
  return { x: left ? MINI_MARGIN : canvas.w - MINI_MARGIN - w, y: MINI_TOP, w, h, scale };
}
